/*
  ============================================================
  Custom: Pick Your Free Blade (cart-free-gift)
  Pairs with snippets/cart-free-gift.liquid.

  Responsibilities (UX only — pricing is enforced by the automatic
  "FREE BLADE" Buy X Get Y discounts at checkout):
    1. Add the chosen knife with the _free_gift property (qty 1).
    2. If a gift is in the cart but the paid subtotal has fallen below
       the threshold for that gift's own tier, remove it and show a
       short notice.
    3. Never allow more than one gift line, or a gift qty above 1.
    4. "Change knife" and "See premium knives" both remove the current
       gift so the picker shows again. There can be TWO such buttons on
       screen at once (the claimed panel and the tier 2 upgrade prompt),
       so every one of them is bound, not just the first.
  A single shared promise chain serialises every mutation so two rapid
  cart changes can't race each other into a double add/remove.
  ============================================================
*/
class CartFreeGift extends HTMLElement {
  static queue = Promise.resolve();
  static NOTICE_KEY = "tcg-free-gift-removed";

  constructor() {
    super();
    this.cart = () => document.querySelector("cart-component");

    this.querySelectorAll(".cart-free-gift-form").forEach((form) => {
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        this.addGift(form);
      });
    });

    // querySelectorAll, not querySelector: the tier 2 upgrade prompt adds a
    // second button with this class and it must work too.
    this.querySelectorAll(".cart-free-gift-change").forEach((btn) => {
      btn.addEventListener("click", (event) => {
        event.preventDefault();
        const trigger = event.currentTarget;
        const key = trigger.dataset.lineItemKey;
        if (!key) return;
        trigger.classList.add("loading");
        trigger.disabled = true;
        this.enqueue(() => this.cart().change(key, 0));
      });
    });

    this.showPendingNotice();
    this.enforceRules();
  }

  enqueue(task) {
    CartFreeGift.queue = CartFreeGift.queue.then(task).catch((err) => console.warn("[free-gift]", err));
    return CartFreeGift.queue;
  }

  showPendingNotice() {
    try {
      if (sessionStorage.getItem(CartFreeGift.NOTICE_KEY) === "1") {
        sessionStorage.removeItem(CartFreeGift.NOTICE_KEY);
        const notice = this.querySelector(".cart-free-gift-notice");
        if (notice) {
          notice.hidden = false;
          setTimeout(() => (notice.hidden = true), 8000);
        }
      }
    } catch (e) { /* storage unavailable — nothing to show */ }
  }

  async addGift(form) {
    const btn = form.querySelector("button");
    btn.classList.add("loading");
    btn.disabled = true;

    await this.enqueue(async () => {
      // Belt and braces: if a gift somehow exists, swap it rather than stack.
      const existingKey = this.dataset.giftLineKey;
      if (existingKey) {
        await this.cart().change(existingKey, 0);
      }
      const formData = new FormData(form);
      await this.cart().add(formData);
    });
  }

  enforceRules() {
    const eligible = this.dataset.eligible === "true";
    const giftKey = this.dataset.giftLineKey;
    const giftQty = Number(this.dataset.giftLineQty || 0);
    const extraKeys = (this.dataset.extraGiftKeys || "").split(",").filter(Boolean);

    if (!giftKey && !extraKeys.length) return;

    this.enqueue(async () => {
      // Re-read the live cart so a stale render can't trigger a wrong removal.
      const cart = await this.cart().get();
      const stillHasGift = cart.items.some((item) => item.key === giftKey);

      // 3a. Duplicate gift lines → keep the first, drop the rest.
      for (const key of extraKeys) {
        if (cart.items.some((item) => item.key === key)) {
          await this.cart().change(key, 0);
        }
      }

      if (!stillHasGift) return;

      // 2. Below the threshold for this gift's own tier → remove it and flag
      //    a notice for the next render.
      if (!eligible) {
        try { sessionStorage.setItem(CartFreeGift.NOTICE_KEY, "1"); } catch (e) {}
        await this.cart().change(giftKey, 0);
        return;
      }

      // 3b. Gift quantity must be exactly 1.
      if (giftQty > 1) {
        await this.cart().change(giftKey, 1);
      }
    });
  }
}
customElements.define("cart-free-gift", CartFreeGift);
